import { Inject, Injectable } from "@angular/core";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { isEmpty } from "lodash-es";

import { LocalizedRoute, RouteConfig, ROUTER_CONFIG, RouterConfig } from "../config/router-config.model";
import { getAllParents } from "../store/route.util";

const PARAM_PREFIX = ":";

@Injectable({
	providedIn: "root",
})
export class LocalizeRouterService {
	private logger: Log;

	constructor(
		private loggingFactory: LoggingFactory,
		@Inject(ROUTER_CONFIG) private config: RouterConfig
	) {
		this.logger = this.loggingFactory.get("localizeRouterService");
	}

	/**
	 * Generate localized `route.path`.
	 * and constructing the URL recursively using parent segments.
	 *
	 * It will change the original objects.
	 */
	localizeRoutes(routes: RouteConfig[]): RouteConfig[] {
		const localizedSegmentLookup: Record<string, string | undefined> = {};
		const routeLookup: Record<string, RouteConfig> = {};

		for (const route of routes) {
			this.validateRoute(route);
			if (routeLookup[route.name]) {
				// we only allow duplication when path is empty, and in that case we know that localization can be skipped
				if (!route.path) {
					continue;
				}

				throw new Error(`[LocalizeRouterService] Cannot register route '${route.name}' as it already exists.`);
			}
			routeLookup[route.name] = route;
			localizedSegmentLookup[route.name] = this.getLocalizedSegment(
				route,
				this.config.localizedRoutes[route.name]
			);
		}

		for (const route of routes) {
			route.originalPath = route.path;
			route.path = this.buildPathFromParents(route, routeLookup, localizedSegmentLookup);
		}

		return routes;
	}

	private validateRoute(route: RouteConfig) {
		if (!route.name) {
			throw new Error(`[LocalizeRouterService] Route has no name defined`);
		}

		if (!route.component && !route.loadComponent && !route.loadChildren && !route.children) {
			throw new Error(
				`[LocalizeRouterService] Route '${route.name}' has no 'component', 'loadComponent', 'loadChildren' or 'children' set`
			);
		}

		if (route.redirectTo) {
			// currently we don't use this functionality
			// if needed: `route.data.redirectTo` can be set to the `name` and that we could resolve during bootstrap
			// to a localized path and set `route.redirectTo` accordingly
			throw new Error(
				`[LocalizeRouterService] Route '${route.name}' has 'redirectTo' which is not yet supported`
			);
		}
	}

	private getLocalizedSegment(route: RouteConfig, localizedRoute: LocalizedRoute): string | undefined {
		const slug = localizedRoute?.slug;

		if (isEmpty(slug) || isEmpty(route.path)) {
			return route.path;
		}

		if (route.path!.indexOf(PARAM_PREFIX) > -1) {
			this.logger.warn(
				"getLocalizedSegment",
				"route contains a param and have slug set. returning original segment",
				{
					name: route.name,
					slug,
				}
			);
			return route.path;
		}

		return slug;
	}

	private buildPathFromParents(
		route: RouteConfig,
		allRoutes: Record<string, RouteConfig>,
		localizedSegmentLookup: Record<string, string | undefined>
	): string {
		return [...getAllParents(route, allRoutes), route]
			.map((x) => localizedSegmentLookup[x.name])
			.join("/")
			.replace(/^\//, "");
	}
}
